import { IconButton, TextButton } from '@creditornot/cb-button';
import { MoveObject } from '@creditornot/cb-icons';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { useDirection } from '@creditornot/cb-provider';
import { DragOverlay, Modifier } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { equals } from 'ramda';
import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';

import { getItemConfigurations } from 'apis/picker-api/api';
import { usePluralization, usePrevious } from 'common/hooks';

import ItemRow from './ItemRow';
import ItemsDragLayer from './ItemsDragLayer';
import PickingListRowRenderer, { RowRendererData } from './PickingListRowRenderer';
import useItemSelection from '../hooks/useItemSelection';
import { ListElement, ItemData } from '../types';
import { isItemType } from '../utils';

type Props = {
  items: ListElement[];
  canSort?: boolean;
  menuId: string | null;
  draggedItemIds?: string[];
  onMoveItems: (itemsData: ItemData[]) => void;
  onRemoveItem?: (itemId: string) => void;
};

const ListRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 20px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ItemRowDragOverlay = styled(ItemRow)`
  ${elevationCss(6)};
`;

const snapDragOverlayToPointer: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent) {
    return transform;
  }
  const activatorCoordinates = getEventCoordinates(activatorEvent);

  if (!activatorCoordinates) {
    return transform;
  }

  const yModifier = activatorCoordinates.y - draggingNodeRect.top - draggingNodeRect.height / 2;

  return {
    ...transform,
    y: transform.y + yModifier,
  };
};

const ItemList = ({ items, canSort, menuId, draggedItemIds, onMoveItems, onRemoveItem }: Props) => {
  const listRef = useRef<VariableSizeList>(null);

  const cbDirection = useDirection();

  const { FormattedPluralMessage } = usePluralization();

  const itemIds = useMemo(() => {
    const itemTypeIds = items.filter(isItemType).map(item => item.data.id);
    return [...new Set([...itemTypeIds, ...(draggedItemIds || [])])];
  }, [draggedItemIds, items]);

  const { selectedItemIds, handleSelectItemIds, handleDeselectItemIds, handleCheckHeader, handleCheckItem } =
    useItemSelection(itemIds);

  const singleDraggedItem = useMemo(
    () => draggedItemIds?.length === 1 && items.find(item => item.data.id === draggedItemIds[0]),
    [draggedItemIds, items],
  );

  const selectedItemData = useMemo(
    () => items.filter(isItemType).filter(item => selectedItemIds.includes(item.data.id)),
    [items, selectedItemIds],
  );

  const { data: itemConfigurations = [] } = useQuery(
    ['item-configurations', menuId],
    () => (menuId ? getItemConfigurations(menuId) : undefined),
    {
      enabled: !!menuId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const virtualListHeights = useMemo(() => items.map(el => (el.type === 'header' ? 64 : 104)), [items]);
  const previousVirtualListHeights = usePrevious(virtualListHeights);

  useEffect(() => {
    if (!equals(virtualListHeights, previousVirtualListHeights)) {
      /* Recalculates heights after dragging items */
      listRef.current?.resetAfterIndex(0);
    }
  }, [previousVirtualListHeights, virtualListHeights]);

  return (
    <>
      <Toolbar>
        {itemIds.length === selectedItemIds.length ? (
          <TextButton variant="negative" onClick={() => handleDeselectItemIds(selectedItemIds)}>
            <FormattedPluralMessage id="picking-route.deselect-item-count" count={itemIds.length} />
          </TextButton>
        ) : (
          <TextButton variant="brand" onClick={() => handleSelectItemIds(itemIds)}>
            <FormattedPluralMessage id="picking-route.select-item-count" count={itemIds.length} />
          </TextButton>
        )}
        {selectedItemIds.length > 0 && (
          <IconButton variant="neutral" size="small" onClick={() => onMoveItems(selectedItemData)}>
            <MoveObject />
          </IconButton>
        )}
      </Toolbar>
      <ListRoot>
        <SortableContext items={items.map(item => item.data)} strategy={verticalListSortingStrategy}>
          <AutoSizer disableWidth>
            {({ height }) => (
              <VariableSizeList<RowRendererData>
                ref={listRef}
                style={{ paddingBlockEnd: 105, direction: cbDirection }}
                height={height}
                width="100%"
                itemData={{
                  list: items,
                  canSort,
                  itemConfigurations,
                  selectedItemIds,
                  onCheckHeader: handleCheckHeader,
                  onCheckItem: handleCheckItem,
                  onMoveItem: itemId => onMoveItems([itemId]),
                  onRemoveItem,
                }}
                itemCount={items.length}
                itemSize={index => virtualListHeights[index]}
                itemKey={(index, data) => {
                  const row = data.list[index];
                  return row.type === 'item' ? row.data.id : row.data.name;
                }}
                overscanCount={6}
              >
                {PickingListRowRenderer}
              </VariableSizeList>
            )}
          </AutoSizer>
        </SortableContext>
        {createPortal(
          <DragOverlay modifiers={[snapDragOverlayToPointer]}>
            {singleDraggedItem && isItemType(singleDraggedItem) && (
              <ItemRowDragOverlay
                itemData={singleDraggedItem}
                canSort={canSort}
                selectedItemIds={selectedItemIds}
                storageLocation={
                  itemConfigurations.find(config => config.itemId === singleDraggedItem.data.id)?.storageLocation ||
                  null
                }
                onMoveItem={() => ({})}
              />
            )}
            {draggedItemIds && draggedItemIds.length > 1 && <ItemsDragLayer count={draggedItemIds.length} />}
          </DragOverlay>,
          document.body,
        )}
      </ListRoot>
    </>
  );
};

export default ItemList;
